import {useMemo, useCallback} from 'react'
import {translate} from 'i18n'
import {requestData} from 'services'
import {TreePlantTreeParams} from 'types'
import {showSnackbar} from '../../components'
import {handleUploadSingleFileToWasabi} from '../../files'

export function usePlantTree({
  mainForm,
  imageForm,
  videoForm,
  srcState,
  stateLoading,
}: TreePlantTreeParams) {
  const {watch} = mainForm
  const {watch: watchVid} = videoForm
  const {watch: watchImg} = imageForm

  const {
    aliases,
    fotoTreeCategory,
    fotoTreeSubcategory,
    fototreeBio,
    fototreeName,
    latitude,
    longitude,
    eventEndDate,
    eventStartDate,
    cityName,
  } = watch()

  const {
    extension: vidExt,
    height: vidHeight,
    size: vidSize,
    width: vidWidth,
    file: vidFile,
    mime: vidMime,
  } = watchVid()

  const {
    imgExtension,
    imgFile,
    imgHeight,
    imgMime,
    imgSize,
    imgWidth,
  } = watchImg()

  const formattedStartDate = useMemo(() => {
    if (!eventStartDate) return ''
    const [year, month, day] = eventStartDate.split('-')
    return `${day}/${month}/${year}`
  }, [eventStartDate])

  const formattedEndDate = useMemo(() => {
    if (!eventEndDate) return ''
    const [year, month, day] = eventEndDate?.split('-')
    return `${day}/${month}/${year}`
  }, [eventEndDate])

  const getVideoLink = useCallback(async () => {
    const response = await requestData(
      'tree_post_plant_fototree_profile_link',
      {
        useDefaultMessage: true,
        actionType: 'execute',
        data: {
          is_intro: true,
          video_intro: {
            height: vidHeight,
            extension: vidExt,
            size: vidSize,
            width: vidWidth,
          },
        },
      },
    )
    return typeof response !== 'string' && response.status === 200
      ? response.data.result.video_intro_url
      : null
  }, [vidExt, vidHeight, vidSize, vidWidth])

  const getProfileLink = useCallback(async () => {
    const response = await requestData(
      'tree_post_plant_fototree_profile_link',
      {
        useDefaultMessage: true,
        actionType: 'execute',
        data: {
          profile: {
            extension: imgExtension,
            height: imgHeight,
            size: imgSize,
            width: imgWidth,
          },
        },
      },
    )
    return typeof response !== 'string' && response.status === 200
      ? response.data.result.profile_url
      : null
  }, [imgExtension, imgHeight, imgSize, imgWidth])

  const handlePublishToWasabi = useCallback(
    async (
      link: string,
      width: number,
      height: number,
      file?: File,
      mime?: string,
    ) => {
      if (file && mime) {
        try {
          return handleUploadSingleFileToWasabi(file, link, mime, {
            'Content-Type': mime,
            'x-amz-meta-imagewidth': width.toString(),
            'x-amz-meta-imageheight': height.toString(),
          })
        } catch (error) {
          return false
        }
      }
      return false
    },
    [],
  )

  const handlePublishVideo = useCallback(async () => {
    const videoLink = await getVideoLink()
    if (videoLink) {
      const videoWasabiStatus = await handlePublishToWasabi(
        videoLink,
        vidWidth,
        vidHeight,
        vidFile,
        vidMime,
      )
      if (videoWasabiStatus) {
        return videoLink
      }
    }
    showSnackbar(translate('global:messageError-execute'))
    stateLoading && stateLoading[1](false)
    return undefined
  }, [
    getVideoLink,
    handlePublishToWasabi,
    stateLoading,
    vidFile,
    vidHeight,
    vidMime,
    vidWidth,
  ])

  const handlePublishProfile = useCallback(async () => {
    const imgLink = await getProfileLink()
    if (imgLink) {
      const imgWasabiStatus = await handlePublishToWasabi(
        imgLink,
        imgWidth,
        imgHeight,
        imgFile,
        imgMime,
      )
      if (imgWasabiStatus) {
        return imgLink
      }
    }
    showSnackbar(translate('global:messageError-execute'))
    stateLoading && stateLoading[1](false)
    return undefined
  }, [
    getProfileLink,
    handlePublishToWasabi,
    imgFile,
    imgHeight,
    imgMime,
    imgWidth,
    stateLoading,
  ])

  const handleSubmitForm = useCallback(async () => {
    const imgLink = await handlePublishProfile()
    const vidLink = srcState[0] ? await handlePublishVideo() : undefined

    if (imgLink) {
      const response = await requestData('tree_post_plant_fototree', {
        useDefaultMessage: true,
        actionType: 'execute',
        data: {
          aliases,
          category_id: fotoTreeSubcategory
            ? fotoTreeSubcategory.id
            : fotoTreeCategory.id,
          location: JSON.stringify({longitude, latitude}),
          profile_size: imgSize,
          profile_url: imgLink,
          tree_bio: fototreeBio,
          tree_name: fototreeName,
          city_name: cityName,
          event_start_date: formattedStartDate,
          event_end_date: formattedEndDate,
          video_intro: vidLink
            ? {
                height: vidHeight,
                meta: undefined,
                size: vidSize,
                url: vidLink,
                width: vidWidth,
              }
            : undefined,
        },
        onRequestReceived: () => stateLoading && stateLoading[1](false),
      })
      return typeof response !== 'string'
        ? {status: response.status, detail: response.data.detail}
        : {status: undefined, detail: undefined}
    }

    return {status: undefined, detail: undefined}
  }, [
    aliases,
    cityName,
    formattedEndDate,
    formattedStartDate,
    fotoTreeCategory,
    fotoTreeSubcategory,
    fototreeBio,
    fototreeName,
    handlePublishProfile,
    handlePublishVideo,
    imgSize,
    latitude,
    longitude,
    srcState,
    stateLoading,
    vidHeight,
    vidSize,
    vidWidth,
  ])

  return useMemo(
    () => ({
      handleSubmitForm,
    }),
    [handleSubmitForm],
  )
}
